/* eslint-disable max-lines-per-function */
import { useCallback } from 'react';

import type { PerimeterXEnforcerChallengeWithBlockedUrl } from '@change-corgi/core/perimeterx';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import type { NormalizedFormValues } from '#src/app/pages/petitionDetails/shared/types';
import { useGenerateSuccessRedirectUrl, useSignPetition as useSignPetitionBase } from '#src/app/shared/hooks/signature';

import { useSignatureTracking } from '../../tracking/useSignatureTracking';
import { useGenerateGuestRedirectUrl } from '../useGenerateGuestRedirectUrl';
import { useSetLocalStoredPendingSignature } from '../useSetLocalStoredPendingSignature';
import { useSetPublicSignatureCookie } from '../useSetPublicSignatureCookie';
import { useSetSessionStoredConstituentStatus } from '../useSetSessionStoredConstituentStatus';
import { useSetSessionStoredReasonForSigning } from '../useSetSessionStoredReasonForSigning';
import { useSignData } from '../useSignData';

export type SignPetitionHookResult =
	| { status: 'success'; redirectUrl: string }
	| { status: 'error'; reason: 'other'; error: unknown }
	| { status: 'error'; reason: 'badSession' | 'captcha' | 'invalidEmail' }
	| { status: 'error'; reason: 'perimeterx'; pxResponse: PerimeterXEnforcerChallengeWithBlockedUrl };

export function useSignPetition(): (formData: NormalizedFormValues) => Promise<SignPetitionHookResult> {
	const { data } = usePetitionDetailsPageData();
	const petitionId = data.petition.id;

	const { errorReporter } = useUtilityContext();

	const signData = useSignData();

	const signPetition = useSignPetitionBase(petitionId, { bandits: true, pageContext: 'petitions_show' });

	const generateGuestPsfUrl = useGenerateGuestRedirectUrl();
	const generateSuccessRedirectUrl = useGenerateSuccessRedirectUrl({ conserveRecruiter: true });

	const setLocalStoredPendingSignature = useSetLocalStoredPendingSignature();
	const setSessionStoredReasonForSigning = useSetSessionStoredReasonForSigning();
	const setSessionStoredConstituentStatus = useSetSessionStoredConstituentStatus();
	const setPublicSignatureCookie = useSetPublicSignatureCookie();

	const { trackDoubleOptIn, trackExistingEmail, trackPerimeterX } = useSignatureTracking();

	return useCallback(
		async (formData) => {
			try {
				const signResult = await signPetition({ signature: { ...formData, ...signData } });

				if (signResult.status === 'error') {
					switch (signResult.reason) {
						case 'grayListed':
							setLocalStoredPendingSignature({ formData });
							setPublicSignatureCookie(formData.public);
							trackDoubleOptIn();
							return {
								status: 'success',
								redirectUrl: generateGuestPsfUrl(formData, { guest: 'new' }),
							};
						case 'guestSignByExistingUser': {
							const { user } = signResult;
							setLocalStoredPendingSignature({ formData, user });
							setPublicSignatureCookie(formData.public);
							trackExistingEmail(user);
							// eslint-disable-next-line max-depth
							if (signResult.pendingAvailable) {
								// this will result in sending an email to the user with a confirmation link
								await signPetition({
									signature: {
										...formData,
										...signData,
										resubmit: true,
										pendingReason: 'RETURNING_GUEST_USER',
									},
								});
							}
							// when email with confirmation link is not sent, this redirects to the PSF which should show a banner with a "log in" action (based on the contents of the pending signature in the localStorage) the PSF will take care of confirming the signature after log in
							return {
								status: 'success',
								redirectUrl: generateGuestPsfUrl(formData, { guest: 'existing' }),
							};
						}
						case 'perimeterx':
							trackPerimeterX();
							return { status: 'error', reason: 'perimeterx', pxResponse: signResult.pxResponse };
						case 'badSession':
						case 'captcha':
						case 'invalidEmail':
							return signResult;
						default:
					}
				}

				setSessionStoredReasonForSigning(formData);
				setLocalStoredPendingSignature(null);
				setPublicSignatureCookie(formData.public);
				setSessionStoredConstituentStatus(formData);

				return {
					status: 'success',
					redirectUrl: generateSuccessRedirectUrl(signResult.redirectUrl),
				};
			} catch (error) {
				// only unexpected errors reach this catch — known outcomes (badSession, captcha,
				// invalidEmail, perimeterx, ...) are returned, not thrown by signPetition — so report
				// them to Sentry with distinct sign-form context before surfacing as a submit error (CHANGE-86207)
				void errorReporter.report({
					error: error instanceof Error ? error : new Error('Sign form submission failed', { cause: error }),
					context: { feature: 'sign_form' },
					params: { petitionId },
				});
				return { status: 'error', reason: 'other', error };
			}
		},
		[
			errorReporter,
			petitionId,
			signPetition,
			signData,
			setSessionStoredReasonForSigning,
			setSessionStoredConstituentStatus,
			setLocalStoredPendingSignature,
			setPublicSignatureCookie,
			generateGuestPsfUrl,
			generateSuccessRedirectUrl,
			trackExistingEmail,
			trackDoubleOptIn,
			trackPerimeterX,
		],
	);
}
